/**
 * Zoom de l'affichage (Réglages › Zoom, préférence `zoom` : Bureau / Mobile, demande du 30/10/2026). Toute l'application
 * est rendue au facteur ZOOM_REFERENCE × zoom choisi, par la propriété CSS `zoom` de la racine (variable --agx-zoom,
 * app.css › Zoom de l'affichage) : 100 % = ancien zoom navigateur de 90 %, pour afficher plus de contenu à l'écran.
 * Le zoom du navigateur (Ctrl +) s'y ajoute.
 *  - Bureau ou Mobile selon l'appareil (plus petit côté de l'écran ≤ 640 px : téléphone), pas selon la fenêtre : réduire
 *    la fenêtre d'un ordinateur ou tourner le téléphone ne change pas le zoom.
 *  - Les points de rupture (media queries, useLargeur) restent ceux de l'écran : même disposition qu'avant, en plus dense.
 *  - Coordonnées : getBoundingClientRect et clientX sont à l'échelle de l'écran, les longueurs CSS à celle de la page
 *    (zoomRacine, design/base.ts).
 *  - Le dernier zoom appliqué est gardé sur l'appareil : écran de connexion et démarrage sans saut d'affichage.
 */
import React from 'react';
import type { Preferences } from '@agora/shared';

/** Facteur du zoom 100 % (l'ancien zoom navigateur de 90 %). */
export const ZOOM_REFERENCE = 0.9;
const COTE_MAX_TELEPHONE = 640;
const CLE = 'agora.zoom';

export type EcranZoom = 'bureau' | 'mobile';

/** Réglage qui s'applique à cet appareil : Mobile sur un téléphone, Bureau ailleurs (ordinateur, tablette). */
export function ecranZoom(): EcranZoom {
  const s = window.screen;
  return s && s.width > 0 && Math.min(s.width, s.height) <= COTE_MAX_TELEPHONE ? 'mobile' : 'bureau';
}

/** Facteur CSS du zoom choisi pour cet écran (100 % → 0,9). */
export function facteurZoom(zoom: Preferences['zoom'], ecran: EcranZoom): number {
  return Number(((ZOOM_REFERENCE * zoom[ecran]) / 100).toFixed(4));
}

function appliquer(facteur: number) {
  document.documentElement.style.setProperty('--agx-zoom', String(facteur));
}

/** Au démarrage, avant le premier affichage : dernier zoom appliqué sur cet appareil (sinon celui d'app.css). */
export function restaurerZoom() {
  try {
    const f = Number(localStorage.getItem(CLE));
    if (f >= 0.5 && f <= 2) appliquer(f);
  } catch { /* ignoré */ }
}

/** Applique le zoom des préférences (Bureau ou Mobile selon l'appareil) et le garde pour le prochain démarrage. */
export function useZoom(zoom: Preferences['zoom'] | undefined) {
  const bureau = zoom?.bureau, mobile = zoom?.mobile;
  React.useEffect(() => {
    if (!bureau || !mobile) return;
    const f = facteurZoom({ bureau, mobile }, ecranZoom());
    appliquer(f);
    try { localStorage.setItem(CLE, String(f)); } catch { /* ignoré */ }
  }, [bureau, mobile]);
}
